import type { OwnerFact, OwnerFactCategory } from '@secretario/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { describeError } from './api/http.js';
import {
  confirmOwnerFact,
  createOwnerFact,
  deleteOwnerFact,
  getOwnerFacts,
  updateOwnerFact,
} from './api/personal.js';
import { FACTS_KEY } from './ownerFacts.js';

/**
 * A lógica de "Sobre você" e do Caderno num lugar só: buscar, editar, apagar,
 * adicionar e confirmar lembranças. Cada tela desenha do seu jeito por cima.
 */
export function useOwnerFacts() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: FACTS_KEY, queryFn: getOwnerFacts });
  return {
    query,
    facts: query.data?.facts ?? [],
    refresh: () => void queryClient.invalidateQueries({ queryKey: FACTS_KEY }),
  };
}

/** Editar e apagar uma lembrança (apagar pede um segundo toque). */
export function useFactEditor(
  fact: OwnerFact,
  { onSaved, onDeleted }: { onSaved: () => void; onDeleted: () => void },
) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(fact.label);
  const [value, setValue] = useState(fact.value);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function save() {
    if (!label.trim() || !value.trim()) return;
    setBusy(true);
    setError('');
    try {
      await updateOwnerFact(fact.id, { label: label.trim(), value: value.trim() });
      setEditing(false);
      onSaved();
    } catch (err) {
      setError(describeError(err, 'Não consegui salvar agora. Tenta de novo?'));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setBusy(true);
    try {
      await deleteOwnerFact(fact.id);
      onDeleted();
    } catch (err) {
      setError(describeError(err, 'Não consegui apagar agora. Tenta de novo?'));
      setBusy(false);
    }
  }

  return {
    editing,
    label,
    value,
    setLabel,
    setValue,
    confirmDelete,
    busy,
    error,
    canSave: !busy && !!label.trim() && !!value.trim(),
    start: () => {
      setLabel(fact.label);
      setValue(fact.value);
      setError('');
      setEditing(true);
    },
    cancel: () => {
      setEditing(false);
      setLabel(fact.label);
      setValue(fact.value);
      setConfirmDelete(false);
    },
    save,
    remove,
  };
}

/** Adicionar uma lembrança numa área. */
export function useFactAdder(
  category: OwnerFactCategory,
  {
    open,
    initialLabel,
    setOpen,
    onSaved,
  }: {
    open: boolean;
    initialLabel: string;
    setOpen: (value: boolean) => void;
    onSaved: () => void;
  },
) {
  const [label, setLabel] = useState(initialLabel);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) setLabel(initialLabel);
  }, [open, initialLabel]);

  async function save() {
    if (!label.trim() || !value.trim()) return;
    setBusy(true);
    setError('');
    try {
      await createOwnerFact({ category, label: label.trim(), value: value.trim() });
      setLabel('');
      setValue('');
      setOpen(false);
      onSaved();
    } catch (err) {
      setError(describeError(err, 'Não consegui salvar agora. Tenta de novo?'));
    } finally {
      setBusy(false);
    }
  }

  return {
    label,
    value,
    setLabel,
    setValue,
    busy,
    error,
    canSave: !busy && !!label.trim() && !!value.trim(),
    cancel: () => {
      setOpen(false);
      setError('');
    },
    save,
  };
}

/**
 * "É isso mesmo": a pessoa confirma o que o Secretário anotou conversando. A
 * lista troca na hora pela resposta do servidor; se falhar, a linha diz o porquê.
 */
export function useFactConfirm() {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState<string | null>(null);
  const [failed, setFailed] = useState<{ id: string; message: string } | null>(null);

  async function confirm(fact: OwnerFact): Promise<boolean> {
    if (confirming) return false;
    setFailed(null);
    setConfirming(fact.id);
    try {
      const saved = await confirmOwnerFact(fact.id);
      queryClient.setQueryData<{ facts: OwnerFact[] }>(FACTS_KEY, (previous) =>
        previous
          ? { facts: previous.facts.map((item) => (item.id === saved.id ? saved : item)) }
          : previous,
      );
      return true;
    } catch (err) {
      setFailed({
        id: fact.id,
        message: describeError(err, 'Não consegui confirmar agora. Tenta de novo?'),
      });
      return false;
    } finally {
      setConfirming(null);
    }
  }

  return { confirm, confirming, failed };
}
